import {
    AppleAppStoreDefaultSettings,
    AppPlatform,
    AppStoreType,
    BetaTestingStyle,
    PatternToken,
    GoogleAppStoreSettings,
    ReleaseMethod,
    ReleaseStepType,
    SettingRadioDirection,
    SettingField,
    SettingFieldType,
    SettingHelpTextBehavior,
} from '@/types';
import {
    BetaTestingStepConfig,
    CIDistributionStepConfig,
    InitialEncryptionOptions,
    MetadataStepConfig,
    RegressionTestingStepConfig,
    ReleaseStepSettings,
    RolloutStepConfig,
    ScreenshotsStepConfig,
    StepConfig,
    StepConfigSummary,
} from '@/types/stepConfig';
import { getIsAppleAppStore, getMetadataReleaseNotesMaxLength } from '@/helpers';
import { allCases } from '@/typescriptHelpers';
import { convertAndroidStagedRolloutPercentage, displayTextForReleaseMethod } from '@/helpers/displayHelpers';
import { COPYRIGHT_YEAR_TOKEN } from '@/constants';

const SECONDS_PER_HOUR = 3600;
const DEFAULT_BETA_SOAK_DURATION_HOURS = 24;

const DEFAULT_METADATA_HELP_TEXT =
    "You can configure the 'Populate store metadata to new releases' automation to use this default text for new releases.";
const PROMO_TEXT_MAX_LENGTH = 170;

const UPSTREAM_CI_BUILD_USAGE_BY_ARCHETYPE: Partial<Record<ReleaseStepType, string>> = {
    [ReleaseStepType.Submission]: 'app submission',
    [ReleaseStepType.StoreReview]: 'store review',
    [ReleaseStepType.Takeoff]: 'release',
};

const stepConfigSummaryOptions = (options: Record<string, StepConfigSummary> | undefined) =>
    Object.entries(options ?? {}).map(([value, summary]) => ({ value, label: summary.name }));

const requiresManualApprovalField = (requiresManualApproval: boolean | null | undefined): SettingField => ({
    key: 'requiresManualApproval',
    type: SettingFieldType.Switch,
    label: 'Require manual approval',
    currentValue: requiresManualApproval ?? true,
    options: null,
});

const upstreamCIStepConfigField = (
    buildUsage: string,
    currentValue: string | null | undefined,
    options: Record<string, StepConfigSummary> | undefined,
): SettingField => ({
    key: 'upstreamCIStepConfigId',
    type: SettingFieldType.Select,
    label: 'Upstream CI step',
    helpText: `Step that will produce builds used for ${buildUsage}.`,
    options: stepConfigSummaryOptions(options),
    currentValue,
});

const buildBranchField: SettingField = {
    key: 'buildBranch',
    type: SettingFieldType.Input,
    label: 'Build from branch',
    helpText:
        'Leave empty to build from the release branch, or enter a different branch to trigger and find this step’s builds on that branch instead.',
    patternTokens: [
        PatternToken.Version,
        PatternToken.VersionConcise,
        PatternToken.VersionMajorMinor,
        PatternToken.VersionMajor,
    ],
    currentValue: null,
    options: null,
};

export const demoStepConfigSettingFields = (stepConfig: StepConfig): SettingField[] => {
    switch (stepConfig.archetype) {
        case ReleaseStepType.ReleaseCandidate:
            return [buildBranchField];
        case ReleaseStepType.CIDistribution: {
            const isReactNativeOTA = stepConfig.destinations.some(
                (destination) => destination.platform === AppPlatform.ReactNativeOTA,
            );
            if (!isReactNativeOTA) {
                return [buildBranchField];
            }
            return [
                buildBranchField,
                {
                    key: 'appIdentifier',
                    type: SettingFieldType.Input,
                    label: 'App identifier',
                    helpText:
                        'Bundle ID or package name used to match stability and observability data for this destination.',
                    currentValue: (stepConfig as CIDistributionStepConfig).settings?.appIdentifier,
                    options: null,
                },
            ];
        }
        case ReleaseStepType.BetaTesting: {
            const settings = (stepConfig as BetaTestingStepConfig).settings;
            return [
                {
                    key: 'betaTestingStyle',
                    type: SettingFieldType.Select,
                    label: 'Beta testing style',
                    required: true,
                    options: [
                        {
                            value: BetaTestingStyle.NonBlocking,
                            label: 'Non-blocking',
                            helpText:
                                'No soak period. Step will be set to a green status as soon as the build is available.',
                        },
                        {
                            value: BetaTestingStyle.OpenEndedSoak,
                            label: 'Open-ended soak',
                            helpText: 'Open-ended beta testing soak period. No minimum soak duration is required.',
                        },
                        {
                            value: BetaTestingStyle.TimeLimitedSoak,
                            label: 'Time-limited soak',
                            helpText: 'The beta build must soak for the given amount of time.',
                        },
                    ],
                    currentValue: settings?.betaTestingStyle,
                },
                {
                    key: 'betaSoakDurationHours',
                    type: SettingFieldType.Integer,
                    label: 'Soak duration',
                    required: true,
                    helpText: 'The soak duration is expressed in hours',
                    helpTextBehavior: SettingHelpTextBehavior.Below,
                    width: 1,
                    min: 1,
                    visibleWhenEquals: { betaTestingStyle: BetaTestingStyle.TimeLimitedSoak },
                    currentValue:
                        settings?.prescribedBetaSoakDurationSeconds ?
                            settings.prescribedBetaSoakDurationSeconds / SECONDS_PER_HOUR
                        :   DEFAULT_BETA_SOAK_DURATION_HOURS,
                    options: null,
                },
                upstreamCIStepConfigField(
                    'beta testing',
                    settings?.upstreamCIStepConfigId,
                    settings?.upstreamCIStepConfigOptions,
                ),
            ];
        }
        case ReleaseStepType.RegressionTesting: {
            const settings = (stepConfig as RegressionTestingStepConfig).settings;
            return [
                upstreamCIStepConfigField(
                    'regression testing',
                    settings?.upstreamCIStepConfigId,
                    settings?.upstreamCIStepConfigOptions,
                ),
            ];
        }
        case ReleaseStepType.Screenshots:
            return [
                requiresManualApprovalField((stepConfig as ScreenshotsStepConfig).settings?.requiresManualApproval),
            ];
        case ReleaseStepType.Metadata: {
            const settings = (stepConfig as MetadataStepConfig).settings;
            const appStoreType = stepConfig.destinations[0]?.appStoreType ?? AppStoreType.None;
            const isAppleAppStore = getIsAppleAppStore(appStoreType);
            const defaultMetadataLocales = settings?.defaultMetadata ?? [];
            const defaultMetadataFields: SettingField[] = [
                ...defaultMetadataLocales.map(
                    (locale): SettingField => ({
                        key: `defaultMetadataByLocale.${locale.locale}.whatsNew`,
                        type: SettingFieldType.TextArea,
                        label: isAppleAppStore ? "Default 'What's new' text" : 'Default release notes',
                        helpText: DEFAULT_METADATA_HELP_TEXT,
                        helpTextBehavior: SettingHelpTextBehavior.Tooltip,
                        maxLength: getMetadataReleaseNotesMaxLength(appStoreType),
                        rows: 10,
                        tab: locale.displayLocale,
                        isLabelAboveTabs: true,
                        currentValue: locale.whatsNew,
                        options: null,
                    }),
                ),
                ...(isAppleAppStore ?
                    defaultMetadataLocales.map(
                        (locale): SettingField => ({
                            key: `defaultMetadataByLocale.${locale.locale}.promoText`,
                            type: SettingFieldType.TextArea,
                            label: 'Default promotional text',
                            helpText: DEFAULT_METADATA_HELP_TEXT,
                            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
                            maxLength: PROMO_TEXT_MAX_LENGTH,
                            rows: 5,
                            tab: locale.displayLocale,
                            isLabelAboveTabs: true,
                            currentValue: locale.promoText,
                            options: null,
                        }),
                    )
                :   []),
            ];
            return [
                requiresManualApprovalField(settings?.requiresManualApproval),
                ...defaultMetadataFields,
                {
                    key: 'upstreamBetaStepConfigId',
                    type: SettingFieldType.Select,
                    label: 'Upstream beta step',
                    helpText: 'Step from which beta tester notes will be retrieved.',
                    options: stepConfigSummaryOptions(settings?.upstreamBetaStepConfigOptions),
                    currentValue: settings?.upstreamBetaStepConfigId,
                },
            ];
        }
        case ReleaseStepType.Rollout: {
            const settings = (stepConfig as RolloutStepConfig).settings;
            return [
                {
                    key: 'sourceStepConfigId',
                    type: SettingFieldType.Select,
                    label: 'Source step',
                    helpText:
                        'Either a Release or Beta step used as the rollout source to determine the builds for which to fetch rollout data.',
                    options: stepConfigSummaryOptions(settings?.sourceStepConfigOptions),
                    currentValue: settings?.sourceStepConfigId,
                },
            ];
        }
        case ReleaseStepType.Submission:
        case ReleaseStepType.StoreReview:
        case ReleaseStepType.Takeoff:
            return demoReleaseStepSettingFields(stepConfig);
        default:
            return [];
    }
};

const franceAvailabilityField = (
    encryptionSelection: InitialEncryptionOptions,
    currentValue: boolean | undefined,
): SettingField => ({
    key: 'isAppAvailableInFrance',
    type: SettingFieldType.Radio,
    label: 'Is your app going to be available on the App Store in France?',
    options: [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' },
    ],
    radioDirection: SettingRadioDirection.Horizontal,
    visibleWhenEquals: { encryptionSelection },
    currentValue,
});

const demoAppStoreSettingFields = (
    stepConfig: StepConfig,
    settings: ReleaseStepSettings | undefined,
): SettingField[] => {
    const destination = stepConfig.destinations[0];
    switch (destination?.appStoreType) {
        case AppStoreType.Apple: {
            const appStoreSettings = settings?.appStoreSettings as AppleAppStoreDefaultSettings | undefined;
            return [
                {
                    key: 'appStoreSettings.releaseMethod',
                    type: SettingFieldType.Select,
                    label: 'Release type',
                    options: allCases(ReleaseMethod).map((releaseMethod) => ({
                        value: releaseMethod,
                        label: displayTextForReleaseMethod(releaseMethod),
                    })),
                    defaultValue: ReleaseMethod.Manual,
                    currentValue: appStoreSettings?.releaseMethod,
                },
                {
                    key: 'appStoreSettings.phasedRelease',
                    type: SettingFieldType.Checkbox,
                    label: 'Phased release enabled',
                    helpText:
                        'Releases this update in stages over a 7-day period to users who have turned on automatic updates.',
                    defaultValue: true,
                    currentValue: appStoreSettings?.phasedRelease,
                    options: null,
                },
                {
                    key: 'encryptionSelection',
                    type: SettingFieldType.Radio,
                    label: 'What type of encryption algorithms does your app implement?',
                    options: [
                        {
                            value: InitialEncryptionOptions.ProprietaryAlgorithms,
                            label: 'Encryption algorithms that are proprietary or not accepted as standard by international standard bodies (IEEE, IETF, ITU, etc.)',
                        },
                        {
                            value: InitialEncryptionOptions.StandardAlgorithms,
                            label: "Standard encryption algorithms instead of, or in addition to, using or accessing the encryption within Apple's operating system",
                        },
                        { value: InitialEncryptionOptions.BothAlgorithms, label: 'Both algorithms mentioned above' },
                        {
                            value: InitialEncryptionOptions.NoAlgorithms,
                            label: 'None of the algorithms mentioned above',
                        },
                    ],
                    currentValue: settings?.encryptionSelection,
                },
                franceAvailabilityField(InitialEncryptionOptions.StandardAlgorithms, settings?.isAppAvailableInFrance),
                franceAvailabilityField(InitialEncryptionOptions.BothAlgorithms, settings?.isAppAvailableInFrance),
                {
                    key: 'appStoreSettings.shouldApplyDefaultEncryption',
                    type: SettingFieldType.Checkbox,
                    label: 'Apply default encryption settings to new builds',
                    currentValue: appStoreSettings?.shouldApplyDefaultEncryption,
                    options: null,
                },
                {
                    key: 'appStoreSettings.copyright',
                    type: SettingFieldType.Input,
                    label: 'Copyright',
                    placeholder: `© ${COPYRIGHT_YEAR_TOKEN} Your Company, Inc.`,
                    currentValue: appStoreSettings?.copyright,
                    options: null,
                },
                { key: 'ageRatingDeclaration', type: SettingFieldType.AgeRating, label: 'Age rating', options: null },
            ];
        }
        case AppStoreType.Google:
        case AppStoreType.Huawei: {
            const appStoreSettings = settings?.appStoreSettings as GoogleAppStoreSettings | undefined;
            const isGooglePlay = destination.appStoreType === AppStoreType.Google;
            return [
                {
                    key: 'appStoreSettings.stagedRelease',
                    type: SettingFieldType.Checkbox,
                    label: 'Staged release',
                    currentValue: appStoreSettings?.stagedRelease,
                    options: null,
                },
                {
                    key: 'appStoreSettings.initialStagedRolloutPercentage',
                    type: SettingFieldType.Number,
                    label: isGooglePlay ? 'Initial staged rollout percentage' : 'Initial phased release percentage',
                    required: true,
                    min: 0.01,
                    max: 100,
                    visibleWhenEquals: { 'appStoreSettings.stagedRelease': true },
                    currentValue:
                        appStoreSettings?.initialStagedRolloutPercentage ?
                            convertAndroidStagedRolloutPercentage(appStoreSettings.initialStagedRolloutPercentage)
                        :   undefined,
                    options: null,
                },
                ...(isGooglePlay ?
                    [
                        {
                            key: 'retainedBuildsSettings',
                            type: SettingFieldType.RetainedBuilds,
                            label: 'Retain builds from previous releases',
                            currentValue: settings?.retainedBuildsSettings,
                            options: null,
                        },
                    ]
                :   []),
            ];
        }
        case AppStoreType.ExpoEAS:
            return stepConfig.archetype === ReleaseStepType.Takeoff ?
                    [
                        {
                            key: 'expoEAS.initialRolloutPercentage',
                            type: SettingFieldType.Integer,
                            label: 'Initial rollout percentage',
                            required: true,
                            min: 1,
                            max: 100,
                            defaultValue: 100,
                            currentValue: settings?.expoEAS?.initialRolloutPercentage,
                            options: null,
                        },
                        {
                            key: 'expoEAS.appIdentifier',
                            type: SettingFieldType.Input,
                            label: 'App identifier',
                            required: true,
                            currentValue: settings?.expoEAS?.appIdentifier,
                            options: null,
                        },
                    ]
                :   [];
        default:
            return [];
    }
};

const demoReleaseStepSettingFields = (stepConfig: StepConfig): SettingField[] => {
    const settings = stepConfig.settings as ReleaseStepSettings | undefined;
    return [
        ...demoAppStoreSettingFields(stepConfig, settings),
        {
            ...upstreamCIStepConfigField(
                UPSTREAM_CI_BUILD_USAGE_BY_ARCHETYPE[stepConfig.archetype] ?? 'this step',
                settings?.upstreamCIStepConfigId,
                settings?.upstreamCIStepConfigOptions,
            ),
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
        },
    ];
};
